import { FC, useCallback, useEffect, useRef, useState } from "react";
import { PointFeature } from "@evergis/sgis/es/features/PointFeature";
import { DrawingFinishEvent } from "@evergis/sgis/es/controls/Control";
import { useMapContext } from "@evergis/react";
import { useTranslation } from "react-i18next";
import { Feature } from "@evergis/sgis";
import { multiLineString, toMercator } from "@turf/turf";
import { isEmpty } from "lodash";
import { LabelFeature } from "@evergis/sgis/es/features/Label";

import { ShadowedPolySymbol } from "../../../../../react-sgis/symbols/editorSymbols/ShadowedPolySymbol";
import { ShadowedPointSymbol } from "../../../../../react-sgis/symbols/editorSymbols/ShadowedPointSymbol";
import { BasePointCreator } from "../../../../../react-sgis/controls/baseControls/BasePointCreator";

import { useGeometrySelection } from "../../../../../hooks/geometrySelection/useGeometrySelection";
import { useGeometrySelectionContext } from "../../../../../hooks/geometrySelection/useGeometrySelectionContext";
import { useSelectionEdit } from "./hooks/useSelectionEdit";
import { useNotification } from "../../../../../hooks/notifications/useNotification";
import { createPointFeature } from "./utils/createPointFeature";
import { createPolylineFeature } from "./utils/createPolylineFeature";
import { createLabelSymbol } from "../../../../../react-sgis/symbols/labelSymbols/labelSymbols";
import { GeometrySelectionSymbolType, useSelectionSymbols } from "./hooks/useSelectionSymbols";

import { newApi } from "../../../../../evergis/api";
import { GeometrySelectionTypes } from "../types";
import { SelectionProps } from "./types";

import {
  CalculatedFeatureTypes,
  SelectionHistoryFeature,
} from "../../../../../ducks/geometrySelection/geometrySelection";

export const RouteSelection: FC<SelectionProps> = ({ mode }) => {
  const { t } = useTranslation("mapControls");
  const [geometrySelection] = useGeometrySelection();
  const { drawFeature, replaceFeature, purgeFeatures } = useGeometrySelectionContext();
  const { map, painter } = useMapContext();
  const createControl = useRef<BasePointCreator>();
  const symbols = useSelectionSymbols();
  const point1 = useRef<SelectionHistoryFeature | null>(null);
  const point2 = useRef<SelectionHistoryFeature | null>(null);
  const [error, setError] = useState<boolean>(false);

  const switchMode = useCallback((create: boolean) => {
    createControl.current.isActive = create;
  }, []);

  const drawRoutePoint = useCallback(
    (event: DrawingFinishEvent<PointFeature>, pointId: number) => {
      drawFeature(
        createPointFeature(
          event.feature.position,
          map.crs,
          symbols[GeometrySelectionSymbolType.point] as ShadowedPointSymbol,
        ),
      );

      drawFeature(
        new LabelFeature(event.feature.position, {
          content: `${pointId}`,
          crs: map.crs,
          symbol: createLabelSymbol("textSymbol", { offset: [0, -10] }),
        }),
      );
    },
    [drawFeature, map.crs, symbols],
  );

  const createFeature = useCallback(
    (event: DrawingFinishEvent<PointFeature>) => {
      if (point1.current === null) {
        setError(false);
        purgeFeatures?.();

        point1.current = event.feature as Feature;

        drawRoutePoint(event, 1);
      } else {
        point2.current = event.feature as Feature;

        drawRoutePoint(event, 2);
      }

      if (point1.current && point2.current) {
        newApi.remoteTaskManager
          .createWorker({
            workerType: "netEngine",
            methodType: "route",
            data: {
              providerName:
                geometrySelection.settings.routeType === CalculatedFeatureTypes.Walking
                  ? "sproute_route_pedestrian"
                  : "sproute_route_car",
              x1: point1.current._position[0],
              y1: point1.current._position[1],
              x2: point2.current._position[0],
              y2: point2.current._position[1],
              srIn: 3857,
              srOut: 4326,
            },
          })
          .then(response => {
            if (
              !Array.isArray(response.coordinates) ||
              isEmpty(response.coordinates[0]) ||
              response.coordinates[0].length < 2
            ) {
              setError(true);
              purgeFeatures?.();

              point1.current = null;
              point2.current = null;

              return;
            }

            const result = createPolylineFeature(
              toMercator(multiLineString(response.coordinates)).geometry.coordinates as [number, number][][],
              map.crs,
              map.resolution,
              symbols[GeometrySelectionSymbolType.polygonEdit] as ShadowedPolySymbol,
              symbols[GeometrySelectionSymbolType.polygonCreate] as ShadowedPolySymbol,
              symbols[GeometrySelectionSymbolType.polylineEdit] as ShadowedPolySymbol,
              symbols[GeometrySelectionSymbolType.polylineCreate] as ShadowedPolySymbol,
            );

            replaceFeature(result);

            point1.current = null;
            point2.current = null;
          })
          .catch(() => {
            setError(true);
            purgeFeatures?.();

            point1.current = null;
            point2.current = null;
          });
      }
    },
    [
      purgeFeatures,
      drawRoutePoint,
      geometrySelection.settings.routeType,
      map.crs,
      map.resolution,
      symbols,
      replaceFeature,
    ],
  );

  const offEvents = useCallback(() => {
    createControl.current.off("drawingFinish", createFeature);
  }, [createFeature]);

  const clearProperties = useCallback(() => {
    createControl.current = undefined;
  }, []);

  const initCreateMode = useCallback(() => {
    if (!createControl.current) {
      createControl.current = new BasePointCreator(map, painter, {
        isActive: false,
        useTempLayer: true,
        symbol: symbols[GeometrySelectionSymbolType.point] as ShadowedPointSymbol,
        noLabel: true,
      });

      createControl.current.on("drawingFinish", createFeature);
    }
  }, [createFeature, map, painter]);

  useNotification(error, {
    error: true,
    title: t("error"),
    description: t("noRoute"),
    duration: 3000,
  });

  useEffect(() => {
    initCreateMode();
    switchMode(true);

    return () => {
      switchMode(false);
      offEvents();
      clearProperties();
    };
  }, [initCreateMode]); // eslint-disable-line

  useSelectionEdit(GeometrySelectionTypes.Line, map, painter, mode, switchMode);

  return null;
};
